import { useState, useCallback } from "react";
import { useGeneratePresignedUrlsMutation } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { getUrlGeneratorInput, uploadFilesToS3 } from "./s3UploadClient";

/**
 * A react hook which returns a function that takes in a list of files and uploads them to
 * the S3 bucket.
 * The function returns a Promise object which will resolve to an array of
 * UploadResponseType instances. Each contain the Response and objectKey for the uploaded file.
 * The objectKey can be used to reference the file in the S3 bucket.
 */
export default function useUploadFilesToS3() {
  const [loading, setLoading] = useState(false);
  // Uploads to S3 bucket require a pre-signed URL. This mutation will take care of
  // generating the pre-signed URL and generating an object key to reference the file.
  const [generatePresignedUrlsMutation] = useGeneratePresignedUrlsMutation({
    context: clientContext(),
  });

  const uploadFiles = useCallback(
    async (files: File[], retryCount = 3) => {
      if (files.length === 0) {
        return [];
      }

      setLoading(true);
      const urlGeneratorInput = getUrlGeneratorInput(files);

      const resp = await generatePresignedUrlsMutation({
        variables: { urlGeneratorInput },
      });

      const presignedObjs = resp.data?.generatePresignedUrls?.presignedUrlObjs || [];
      if (!presignedObjs.length) {
        setLoading(false);
        throw new Error("No presigned URLs generated");
      }

      // Check for any null values in the event of an error
      const filesToUpload = files.map((file, index) => {
        const urlObj = presignedObjs[index];
        if (!urlObj?.s3Url || !urlObj?.objectKey) {
          setLoading(false);
          throw new Error(`Invalid presigned URL data for file: ${file.name}`);
        }

        return {
          generatedUrlObj: {
            s3Url: urlObj.s3Url,
            objectKey: urlObj.objectKey,
            fileName: urlObj.fileName ?? file.name,
          },
          file,
        };
      });
      let uploadedFilesToS3;
      try {
        uploadedFilesToS3 = await uploadFilesToS3(filesToUpload, retryCount);
      } catch (error) {
        throw new Error("Failed to upload files to S3");
      } finally {
        setLoading(false);
      }
      return uploadedFilesToS3;
    },
    [generatePresignedUrlsMutation],
  );

  return { uploadFilesToS3: uploadFiles, loading };
}
